import { useContext, memo } from "react";
import {
  DefaultMantineColor,
  Navbar as MantineNavbar,
  Tabs,
  useMantineTheme,
  Text,
  Flex,
} from "@mantine/core";
import { FaRegAddressCard, FaRegClipboard } from "react-icons/fa";
import { AiOutlineStar } from "react-icons/ai";
import { AppContext } from "src/api/AppContext";
import { UserRole } from "src/generated/graphql";
import { CLIENT_TAB_LIST, ClientCardBg } from "src/utils/constants";
import { BiHomeAlt2, BiLogOut } from "react-icons/bi";
import {} from "@aws-amplify/ui-react";
import { BsCalendar } from "react-icons/bs";
import { IoIosArrowForward } from "react-icons/io";
import { ApolloClientContext } from "src/api/ApolloClientContext";

interface NavbarProps {
  isOpened: boolean;
  setNavbarOpened: (opened: boolean) => void;
}

type TabType = {
  key: string;
  label: string;
  roles?: UserRole[];
  icon?: React.ReactNode;
  color?: DefaultMantineColor;
  elementOverride?: React.ReactNode;
};

const Navbar: React.FC<NavbarProps> = ({
  isOpened,
  setNavbarOpened,
}: NavbarProps) => {
  const { changeClientTab, activeTabKey } = useContext(AppContext);
  const { logout } = useContext(ApolloClientContext);
  const theme = useMantineTheme();

  const Tablist: TabType[] = [
    {
      key: CLIENT_TAB_LIST.HOME,
      label: CLIENT_TAB_LIST.HOME,
      icon: <BiHomeAlt2 size={17} color={theme.colors.secondaryTextColor[6]} />,
    },
    {
      key: CLIENT_TAB_LIST.PROFILE,
      label: CLIENT_TAB_LIST.PROFILE,
      icon: (
        <FaRegAddressCard
          size={17}
          color={theme.colors.secondaryTextColor[6]}
        />
      ),
    },
    {
      key: CLIENT_TAB_LIST.MY_CALENDAR,
      label: CLIENT_TAB_LIST.MY_CALENDAR,
      icon: <BsCalendar size={15} color={theme.colors.secondaryTextColor[6]} />,
    },
    {
      key: CLIENT_TAB_LIST.MY_ACCOUNTS,
      label: CLIENT_TAB_LIST.MY_ACCOUNTS,
      icon: (
        <AiOutlineStar size={18} color={theme.colors.secondaryTextColor[6]} />
      ),
    },
    {
      key: CLIENT_TAB_LIST.MY_RECORDS,
      label: CLIENT_TAB_LIST.MY_RECORDS,
      icon: (
        <FaRegClipboard size={17} color={theme.colors.secondaryTextColor[6]} />
      ),
    },
  ];

  return (
    <MantineNavbar
      p="sm"
      hiddenBreakpoint="md"
      hidden={!isOpened}
      width={{ sm: 220, md: 220, lg: 300 }}
      style={{
        transition: "margin-left 0.3s",
        background: theme.colors.secondary[6],
        color: theme.colors.secondaryTextColor[6],
        borderRight: "none",
      }}
    >
      <MantineNavbar.Section sx={{ flex: 1 }}>
        <Tabs
          orientation="vertical"
          value={activeTabKey}
          onTabChange={(key) => {
            changeClientTab(key || "");
            setNavbarOpened(false);
          }}
        >
          <Tabs.List
            style={{
              flex: 1,
              borderColor: theme.colors.primary[6],
              border: "none",
            }}
            grow
          >
            {Tablist.map((tab) => {
              return (
                <Tabs.Tab
                  key={tab.key}
                  value={tab.key}
                  icon={tab.icon}
                  color={tab.color}
                  bg={theme.colors.secondary[6]}
                  rightSection={
                    <IoIosArrowForward
                      size={15}
                      style={{
                        position: "absolute",
                        right: 4,
                        color: theme.colors.secondaryTextColor[6],
                      }}
                    />
                  }
                  sx={{
                    backgroundColor:
                      activeTabKey === tab.key
                        ? theme.colors.primary[6]
                        : theme.colors.secondary[6],
                    "&:hover": {
                      backgroundColor:
                        activeTabKey === tab.key
                          ? theme.colors.primary[4]
                          : theme.colors.secondary[4],
                    },
                    borderBottom: theme.colors.secondaryTextColor[6],
                    padding: "14px 10px 10px 10px",
                  }}
                  style={{
                    ...{
                      borderRadius: 0,
                    },
                    ...(activeTabKey === tab.key
                      ? {
                          // borderTopLeftRadius: 4,
                          // borderBottomLeftRadius: 4,
                          borderRadius: 4,
                          backgroundColor: theme.colors.primary[6],
                          fontWeight: 500,
                          color: theme.colors.primaryTextColor[6],
                        }
                      : {}),
                  }}
                >
                  <Text
                    size={16}
                    fw={500}
                    pl={6}
                    color={theme.colors.secondaryTextColor[6]}
                  >
                    {tab.label}
                  </Text>
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Tabs>
      </MantineNavbar.Section>

      <MantineNavbar.Section>
        <Flex
          align={"center"}
          gap={16}
          mb={"xs"}
          sx={{
            "&:hover": {
              backgroundColor: theme.colors.secondary[4],
            },
            padding: "12px 4px",
            cursor: "pointer",
            color: theme.colors.secondaryTextColor[6],
          }}
          onClick={logout}
        >
          <BiLogOut
            size={17}
            color={theme.colors.secondaryTextColor[6]}
            style={{ transform: "rotate(180deg)" }}
          />
          <Text size={18} fw={500}>
            {CLIENT_TAB_LIST.LOGOUT}
          </Text>
        </Flex>
      </MantineNavbar.Section>
    </MantineNavbar>
  );
};

export default memo(Navbar);
